import { askConfirm } from '../lib/askConfirm';
import { areaCoverage, needsWater } from '../lib/coverage';
import { HelpBtn } from './HelpBtn';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Sprout, Hexagon, Trash2 } from 'lucide-react';
import { rowCalc, landCalc, ROW_DEF, LAND, PCT } from '../lib/garden';
import { locale } from '../lib/units';
import { uid } from '../lib/model';
import { flyTo } from '../lib/vis';
import { openAutoPlace } from './AutoPlaceBar';
import { openIrrPlan } from './IrrPlan';

export const openGarden = (id = null, tab = null) => { const e = new CustomEvent('giss-garden-open', { detail: id }); e.tab = tab; window.dispatchEvent(e); };
const f = (v, d = 1) => v.toLocaleString(locale(), { minimumFractionDigits: d, maximumFractionDigits: d });
const Num = ({ testid, label, value, ph, onChange }) => <label>{label}<input inputMode="decimal" data-testid={testid} value={value ?? ''} placeholder={String(ph)} onChange={(e) => onChange(e.target.value.replace(',', '.'))} /></label>;
const Row = ({ k, v, u, testid }) => <tr data-testid={testid}><td>{k}</td><td><b>{v}</b> {u}</td></tr>;

function RowTab({ t, g, set }) {
  const c = g.row || {}, r = rowCalc(g.pts, c), s = (k) => (v) => set({ row: { ...c, [k]: v } });
  return (
    <>
      <div className="sw-grid">
        {[['rs', t.gaRs, 'm'], ['ps', t.gaPs, 'm'], ['post', t.gaPost, 'm'], ['drip', t.gaDrip, '%'], ['foil', t.gaFoil, '%'], ['fence', t.gaFence, 'm']].map(([k, l]) => <Num key={k} testid={`garden-row-${k}`} label={l} value={c[k]} ph={ROW_DEF[k]} onChange={s(k)} />)}
        <Num testid="garden-row-rows" label={t.gaRows} value={c.rows} ph={r.rows} onChange={s('rows')} />
        <Num testid="garden-row-len" label={t.gaRowLen} value={c.rowLen} ph={f(r.rowLen)} onChange={s('rowLen')} />
      </div>
      <table className="ga-res" data-testid="garden-row-result"><tbody>
        <Row k={t.gaRows} v={r.rows} u={`× ${f(r.rowLen)} m = ${f(r.total)} m`} testid="ga-rows" />
        <Row k={t.gaPlants} v={r.plants} u={t.unit_pcs} testid="ga-plants" />
        <Row k={t.gaEndPosts} v={r.endPosts} u={`+ ${r.anchors} ${t.gaAnchors}`} testid="ga-endposts" />
        <Row k={t.gaMidPosts} v={r.midPosts} u={t.unit_pcs} testid="ga-midposts" />
        <Row k={t.gaDripLen} v={f(r.drip)} u="m" testid="ga-drip" />
        <Row k={t.gaMain} v={f(r.main)} u="m" testid="ga-main" />
        <Row k={t.gaNet} v={f(r.net)} u="m²" testid="ga-net" />
        <Row k={t.gaFenceLen} v={f(r.fence)} u={`m · ${r.fencePosts} ${t.gaFencePosts}`} testid="ga-fence" />
      </tbody></table>
    </>
  );
}

function LandTab({ t, g, set }) {
  const c = g.land || {}, r = landCalc(g.pts, c), s = (k) => (v) => set({ land: { ...c, [k]: v } });
  const vol = (k) => `m³ · ${f(r[k].t, 2)} t`;
  return (
    <>
      <div className="sw-grid">
        {['turf', 'topsoil', 'mulch', 'gravel', 'paving'].map((k) => <Num key={k} testid={`garden-land-${k}-pct`} label={`${t['ga_' + k]} (% ${t.gaOfArea})`} value={c[k + 'Pct']} ph={PCT[k + 'Pct']} onChange={s(k + 'Pct')} />)}
        {['topsoil', 'mulch', 'gravel'].map((k) => <Num key={k} testid={`garden-land-${k}-cm`} label={`${t['ga_' + k]} (cm)`} value={c[k + 'Cm']} ph={LAND[k].cm} onChange={s(k + 'Cm')} />)}
      </div>
      <table className="ga-res" data-testid="garden-land-result"><tbody>
        <Row k={t.ga_turf} v={f(r.turf)} u="m²" testid="ga-turf" />
        <Row k={t.ga_topsoil} v={f(r.topsoil.m3, 2)} u={vol('topsoil')} testid="ga-topsoil" />
        <Row k={t.ga_mulch} v={f(r.mulch.m3, 2)} u={vol('mulch')} testid="ga-mulch" />
        <Row k={t.ga_gravel} v={f(r.gravel.m3, 2)} u={vol('gravel')} testid="ga-gravel" />
        <Row k={t.ga_paving} v={f(r.paving)} u="m²" testid="ga-paving" />
        <Row k={t.gaEdging} v={f(r.edging)} u="lfm" testid="ga-edging" />
      </tbody></table>
    </>
  );
}

// GaLaBau / agriculture: areas drawn like an excavation pit -> row/orchard and landscaping quantities
export default function GardenCalc({ t, project, updateProject, pits }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState('row');
  const gardens = project.gardens || [];
  useEffect(() => {
    const onOpen = (e) => { setOpen(true); if (e.detail) setSel(e.detail); if (e.tab) setTab(e.tab); };
    const onArea = (e) => {
      const g = { id: uid('ga'), name: `${t.gaArea} ${(project.gardens || []).length + 1}`, pts: e.detail };
      updateProject((p) => ({ ...p, gardens: [...(p.gardens || []), g] }));
      setSel(g.id); setOpen(true); toast.success(t.gaSaved);
    };
    const onPts = (e) => updateProject((p) => ({ ...p, gardens: (p.gardens || []).map((x) => (x.id === e.detail.id ? { ...x, pts: e.detail.pts } : x)) }));
    window.addEventListener('giss-garden-open', onOpen);
    window.addEventListener('giss-garden-area', onArea);
    const onDup = (e) => { updateProject((p) => ({ ...p, gardens: [...(p.gardens || []), e.detail] })); toast.success(t.aeDupOk); };
    window.addEventListener('giss-garden-pts', onPts);
    window.addEventListener('giss-garden-dup', onDup);
    return () => { window.removeEventListener('giss-garden-dup', onDup); window.removeEventListener('giss-garden-open', onOpen); window.removeEventListener('giss-garden-area', onArea); window.removeEventListener('giss-garden-pts', onPts); };
  }, [project.gardens, updateProject, t]);
  if (!open) return null;
  const g = gardens.find((x) => x.id === sel) || gardens[0];
  const set = (p) => updateProject((pr) => ({ ...pr, gardens: pr.gardens.map((x) => (x.id === g.id ? { ...x, ...p } : x)) }));
  const draw = () => { setOpen(false); pits.start('polygon', true); document.querySelector('[data-testid="map-wrapper"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); };
  return createPortal(
    <div className="editor-backdrop" data-testid="garden-dialog">
      <div className="editor garden-dialog">
        <div className="editor-head"><h4><Sprout size={18} /> {t.gaTitle}</h4><HelpBtn k="n:garden_extra" testid="garden-help" title={t.helpHere} /><button className="icon-btn" data-testid="garden-close-btn" onClick={() => setOpen(false)}><X size={16} /></button></div>
        <div className="row">
          {gardens.length > 0 && <select data-testid="garden-select" value={g?.id} onChange={(e) => setSel(e.target.value)}>{gardens.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>}
          <button className="btn btn-green btn-sm" data-testid="garden-draw-btn" onClick={draw}><Hexagon size={14} /> {t.gaDraw}</button>
        </div>
        {!g && <p className="muted" data-testid="garden-empty">{t.gaEmpty}</p>}
        {g && (
          <>
            <div className="row">
              <input data-testid="garden-name-input" value={g.name} onChange={(e) => set({ name: e.target.value })} />
              <button className="btn btn-outline btn-sm" data-testid="garden-locate-btn" onClick={() => { setOpen(false); flyTo(g.pts); }}>📍</button>
              <button className="icon-btn danger" data-testid="garden-delete-btn" onClick={async () => (await askConfirm(t.gaDelQ, { ok: t.pitDelete, cancel: t.btnCancel })) && updateProject((p) => ({ ...p, gardens: p.gardens.filter((x) => x.id !== g.id) }))}><Trash2 size={14} /></button>
            </div>
            <div className="chip-group">
              <button className={`chip seg ${tab === 'row' ? 'on' : ''}`} data-testid="garden-tab-row" onClick={() => setTab('row')}>🍎 {t.gaTabRow}</button>
              <button className={`chip seg ${tab === 'land' ? 'on' : ''}`} data-testid="garden-tab-land" onClick={() => setTab('land')}>🌱 {t.gaTabLand}</button>
            </div>
            <p className="sw-res" data-testid="garden-area">{t.gaAreaLbl}: <b>{f(rowCalc(g.pts).A)} m²</b> · {t.gaPer}: {f(rowCalc(g.pts).per)} m</p>
            <label className="chk-row" data-testid="garden-water-row"><input type="checkbox" data-testid="garden-water-check" checked={needsWater(g)} onChange={(e) => set({ water: e.target.checked })} /> {t.dryNeeds}</label>
            {needsWater(g) && (() => { const c = areaCoverage(g, project.fittings, project.dryStrict); return <><p className="sw-res dry-res" data-testid="garden-coverage">{t.dryCov}: <b className="d">{c.dry} % {t.dryDry}</b> · <b className="w">{c.weak} % {t.dryWeak}</b> · <b className="o">{c.ok} % {t.dryOk}</b></p><label className="chk-row" data-testid="garden-strict-row"><input type="checkbox" data-testid="garden-strict-check" checked={!!project.dryStrict} onChange={(e) => updateProject((p) => ({ ...p, dryStrict: e.target.checked }))} /> {t.dryStrict}</label><p className="muted small-hint" data-testid="garden-strict-hint">{project.dryStrict ? t.dryStrictOn : t.dryStrictOff}</p><div className="row"><button className="btn btn-green btn-sm" data-testid="garden-auto-place-btn" onClick={() => { setOpen(false); openAutoPlace(g.id); }}>🪄 {t.apTitle}</button><button className="btn btn-outline btn-sm" data-testid="garden-irr-plan-btn" onClick={() => { setOpen(false); openIrrPlan(); }}>⏱ {t.izTitle}</button></div></>; })()}
            {tab === 'row' ? <RowTab t={t} g={g} set={set} /> : <LandTab t={t} g={g} set={set} />}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
